import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Loader2, Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { BrandLink } from "@/components/Brand";

/**
 * /reset-password?token=... — landing page reached from the reset-link email.
 * Asks for a new password (twice), submits to /api/auth/reset-password,
 * then redirects to /login on success.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const validation = useMemo(() => {
    if (pw1.length === 0) return { ok: false, msg: "Choose a new password." };
    if (pw1.length < 8) return { ok: false, msg: "Use at least 8 characters." };
    if (pw2 && pw1 !== pw2) return { ok: false, msg: "Passwords don't match yet." };
    if (!pw2) return { ok: false, msg: "Confirm your new password." };
    return { ok: true };
  }, [pw1, pw2]);

  const submit = async (e) => {
    e.preventDefault();
    if (!validation.ok) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      toast.success("Password updated — sign in with your new one.");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        "Couldn't reset your password. The link may have expired — try requesting a new one.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-background">
        <div className="card-default p-6 max-w-md w-full text-center space-y-3" data-testid="reset-password-no-token">
          <div className="w-12 h-12 rounded-full bg-destructive/15 text-destructive grid place-items-center mx-auto">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Reset link missing</h1>
          <p className="text-sm text-muted-foreground">
            This page should be opened from the reset link in your email. If you
            need a new link, head back to the forgot-password page.
          </p>
          <Link to="/forgot-password" className="btn-primary inline-flex w-full" data-testid="reset-password-request-new">
            Request a new link <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1530046614490-89e6f776b83b?auto=format&fit=crop&w=1400&q=80"
          alt="Workshop"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <div className="bg-card rounded-lg p-3 w-fit"><BrandLink /></div>
          <div>
            <div className="label-overline text-white/60">Almost there</div>
            <h2 className="font-display text-4xl font-bold tracking-tight mt-3 leading-tight">
              Choose a new<br /><span className="text-primary">password</span>.
            </h2>
          </div>
          <div className="font-mono-plex text-xs text-white/50">Minimum 8 characters · we'll never email it back to you</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        {done ? (
          <div className="w-full max-w-md space-y-5" data-testid="reset-password-done">
            <div className="w-12 h-12 rounded-full bg-success/15 text-success grid place-items-center">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <div className="label-overline">Done</div>
              <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Password updated</h1>
            </div>
            <p className="text-sm text-muted-foreground">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="reset-password-form">
            <div>
              <div className="label-overline">Reset password</div>
              <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Choose a new password</h1>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">New password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="password"
                  required
                  autoFocus
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  className="input-default pl-9"
                  placeholder="At least 8 characters"
                  data-testid="reset-password-pw1-input"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Confirm new password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="password"
                  required
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="input-default pl-9"
                  placeholder="Type it again"
                  data-testid="reset-password-pw2-input"
                />
              </div>
            </div>

            {!validation.ok && (pw1 || pw2) && (
              <p className="text-xs text-muted-foreground" data-testid="reset-password-hint">{validation.msg}</p>
            )}
            {error && (
              <div className="text-sm text-destructive flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/[0.05] p-3" data-testid="reset-password-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button type="submit" disabled={busy || !validation.ok} className="btn-primary w-full" data-testid="reset-password-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Update password <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-sm text-muted-foreground">
              Changed your mind? <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="reset-password-to-login-link">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
